import { ArrowLeft, ArrowRight, Check, LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { PageHeading } from "../components/PageHeading";
import { useCapabilities } from "../context/CapabilitiesContext";
import { usePortal } from "../context/PortalContext";
import { api } from "../lib/api";
import type { Lesson } from "../types";

export function ReaderPage() {
  const { chapter = "chapter-1" } = useParams();
  const { track, practice, refreshPractice } = usePortal();
  const capabilities = useCapabilities();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let active = true;
    setLesson(null);
    setError(null);
    if (!track) return () => { active = false; };
    api.lesson(track.slug, chapter)
      .then((nextLesson) => {
        if (active) {
          setError(null);
          setLesson(nextLesson);
        }
      })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : String(reason)); });
    return () => { active = false; };
  }, [track?.slug, chapter]);
  const catalogIndex = useMemo(() => track?.lesson_catalog.findIndex((item) => item.id === chapter) ?? -1, [track, chapter]);
  if (!track || !lesson) return <div className="loading-state"><LoaderCircle className="spin" size={20} />{error || "正在读取教材"}</div>;
  const completed = practice?.lesson_progress[chapter]?.completed;

  async function markComplete() {
    setSaving(true);
    setError(null);
    try {
      await api.saveLessonProgress(track!.slug, chapter, true);
      await refreshPractice();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setSaving(false);
    }
  }

  const previous = catalogIndex > 0 ? track.lesson_catalog[catalogIndex - 1] : null;
  const next = catalogIndex + 1 < track.lesson_catalog.length ? track.lesson_catalog[catalogIndex + 1] : null;
  return (
    <>
      <PageHeading number={String(lesson.number).padStart(2, "0")} title={lesson.title} description={`共 ${lesson.sections.length} 节，预计 ${lesson.estimated_minutes} 分钟。阅读完成只记录接触，不代表能力通过。`} />
      <div className="reader-layout">
        <aside className="reader-outline">
          <strong>本章目录</strong>
          {lesson.sections.map((section, index) => <button key={section.id} type="button" onClick={() => document.getElementById(section.id)?.scrollIntoView()}>{index + 1}. {section.title}</button>)}
          <button className="command-button" type="button" disabled={!capabilities.practice.writable || completed || saving} onClick={() => void markComplete()}>
            <Check size={17} />{!capabilities.practice.writable ? "只读模式" : completed ? "已完成阅读" : saving ? "正在保存" : "标记本章已读"}
          </button>
          {error && <p className="form-error" role="alert">{error}</p>}
        </aside>
        <article className="lesson-content">
          {lesson.sections.map((section) => (
            <section id={section.id} key={section.id}>
              <h2>{section.title}</h2>
              {section.figures.map((figure) => <figure key={figure.filename}><img src={figure.url} alt={`${section.title}教学图`} /><figcaption>{figure.caption}</figcaption></figure>)}
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.markdown}</ReactMarkdown>
            </section>
          ))}
          <nav className="reader-navigation" aria-label="教材章节导航">
            {previous ? <Link to={`/learn/reader/${previous.id}`}><ArrowLeft size={17} />上一章：{previous.title}</Link> : <span />}
            {next ? <Link to={`/learn/reader/${next.id}`}>下一章：{next.title}<ArrowRight size={17} /></Link> : <span />}
          </nav>
        </article>
      </div>
    </>
  );
}
